import Dexie from 'dexie'
import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { executeDatabaseOperation } from '@/db/errors'
import { inspectionSchema } from '@/db/schemas'
import { BackupStatusService } from '@/db/services/backupStatusService'
import type { Inspection, InspectionPriority, InspectionStatus } from '@/db/types'
import { DevicePreferencesService } from '@/services/devicePreferences'

export interface HomeInspectionSummary {
  id: string
  reportNumber: string
  status: InspectionStatus
  priority: InspectionPriority
  companyName: string
  machineName: string
  inspectionDate: string
  updatedAt: string
}

export interface HomeDashboardData {
  defaultTechnicianName?: string
  backupPending: boolean
  lastBackupAt?: string
  counts: {
    completedInspections: number
    draftInspections: number
    activeClients: number
    activeMachines: number
    criticalInspections: number
  }
  recentDrafts: HomeInspectionSummary[]
  recentInspections: HomeInspectionSummary[]
}

function toSummary(inspection: Inspection): HomeInspectionSummary {
  return {
    id: inspection.id,
    reportNumber: inspection.reportNumber,
    status: inspection.status,
    priority: inspection.priority,
    companyName: inspection.clientSnapshot.companyName,
    machineName: `${inspection.machineSnapshot.equipment} · ${inspection.machineSnapshot.model}`,
    inspectionDate: inspection.inspectionDate,
    updatedAt: inspection.updatedAt,
  }
}

function recentByStatus(database: RealMachinesDatabase, status: InspectionStatus, limit: number) {
  return database.inspections
    .where('[status+updatedAt]')
    .between([status, Dexie.minKey], [status, Dexie.maxKey])
    .reverse()
    .limit(limit)
    .toArray()
}

export class HomeDashboardService {
  constructor(
    private readonly database: RealMachinesDatabase = getDatabase(),
    private readonly devicePreferences = new DevicePreferencesService(),
  ) {}

  async load(recentLimit = 4): Promise<HomeDashboardData> {
    return executeDatabaseOperation('carregar painel inicial', async () => {
      const limit = Math.min(Math.max(Math.trunc(recentLimit), 1), 10)
      const preferences = this.devicePreferences.get()

      const [
        completedInspections,
        draftInspections,
        activeClients,
        activeMachines,
        criticalInspections,
        recentDraftRecords,
        recentCompletedRecords,
        recentCancelledRecords,
        defaultTechnician,
        backupStatus,
      ] = await Promise.all([
        this.database.inspections.where('status').equals('completed').count(),
        this.database.inspections.where('status').equals('draft').count(),
        this.database.clients.where('activeKey').equals(1).count(),
        this.database.machines.where('activeKey').equals(1).count(),
        this.database.inspections.where('priority').equals('urgent_stop_immediately').count(),
        recentByStatus(this.database, 'draft', limit),
        recentByStatus(this.database, 'completed', limit),
        recentByStatus(this.database, 'cancelled', limit),
        preferences.defaultTechnicianId
          ? this.database.technicians.get(preferences.defaultTechnicianId)
          : Promise.resolve(undefined),
        new BackupStatusService(this.database).getStatus(),
      ])

      const recentInspections = [...recentCompletedRecords, ...recentCancelledRecords]
        .map((record) => inspectionSchema.parse(record))
        .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
        .slice(0, limit)
        .map(toSummary)

      return {
        ...(defaultTechnician?.active ? { defaultTechnicianName: defaultTechnician.name } : {}),
        backupPending: backupStatus.pending,
        ...(backupStatus.lastBackupAt ? { lastBackupAt: backupStatus.lastBackupAt } : {}),
        counts: {
          completedInspections,
          draftInspections,
          activeClients,
          activeMachines,
          criticalInspections,
        },
        recentDrafts: recentDraftRecords
          .map((record) => inspectionSchema.parse(record))
          .map(toSummary),
        recentInspections,
      }
    })
  }
}
